import moment from 'moment';
import groupBy from 'lodash/groupBy';
import { getBeltFromTotalPoints } from '@/belts';
import settings from '@/settings';

function getNextSuggestedSteps(courseIds, rootState, rootGetters) {
    const items = [];
    courseIds.forEach(courseId => {
        const course = rootState.moduleCourse.courses[courseId];
        const courseStats = rootGetters['moduleCourse/getCourseStats'](course.id);

        if (
            courseStats.theoryProgress === 0 &&
            course.introStepId &&
            !rootState.moduleCourse.steps[course.introStepId].progress
        ) {
            items.push({
                type: 'course',
                courseId: course.id,
                firstActiveStepId: course.introStepId
            });
        } else if (!courseStats.firstActiveJourneyId) {
            const nextJourneyId = course.stepGroupIds.find(journeyId => {
                const journeyStats = rootGetters['moduleCourse/getStepGroupStats'](journeyId);
                return journeyStats.theoryProgress !== 1;
            });
            if (nextJourneyId) {
                const stepGroup = rootState.moduleCourse.stepGroups[nextJourneyId];
                items.push({
                    type: 'course',
                    courseId: course.id,
                    firstActiveJourneyId: nextJourneyId,
                    firstActiveStepId: stepGroup.stepIds[0]
                });
            }
        }
    });
    return items;
}

export default {
    appNeedsReload: state => {
        return state.clientsideGitRevision !== state.serversideGitRevision;
    },
    didShowMemoOnboardingPopup: state => state.memolife.didShowMemoOnboardingPopup,
    didShowMemomapsOnboardingPopup: state => state.memolife.didShowMemomapsOnboardingPopup,

    getLifeskillIdByNumbershape: state => numbershape => {
        return state.lifeskillsList.find(item => item.numbershape === numbershape).id;
    },

    getLifeskillById: (state, getters) => id => {
        return state.lifeskills[id];
    },
    getVideoById: (state, getters) => id => {
        return state.videos[id];
    },
    getCategoryById: (state, getters) => id => {
        return state.categories[id];
    },

    getLifeskillIds: (state, getters) => {
        return Object.keys(state.lifeskills);
    },

    getEssentialLifeskills: state => {
        return Object.values(state.lifeskills)
            .filter(lifeskill => lifeskill.isEssential)
            .map(lifeskill => lifeskill.id);
    },

    getVideosForLifeskill: state => lifeskillId => {
        return Object.values(state.videos)
            .filter(video => video.lifeskillIds && video.lifeskillIds.indexOf(lifeskillId) !== -1)
            .map(video => {
                return { id: video.id, ordering: video.ordering[lifeskillId], playlist: video.playlists[lifeskillId] };
            });
    },

    getVideoBraindatesMap(state) {
        const videoBraindates = state.braindates.filter(bd => bd.context === 'video');
        return groupBy(videoBraindates, bd => bd.lifeskillNumber);
    },

    getOnboardingVideoBraindate(state) {
        const bd = state.braindates.find(bd => bd.videoId === settings.onboardingVideoDatabaseId);
        if (bd) {
            return bd;
        }
        return null;
    },

    getLifeskillStats: (state, getters, rootState, rootGetters) => lifeskillId => {
        const lifeskill = getters.getLifeskillById(lifeskillId);
        let courseIds = [];
        if (lifeskill.masterCourseId) {
            courseIds.push(lifeskill.masterCourseId);
        }

        let stats = {
            totalTheoryBraindates: 0,
            totalPracticeBraindates: 0,
            totalTheoryLifekeys: 0,
            totalPracticeLifekeys: 0,

            acquiredTheoryBraindates: 0,
            acquiredPracticeBraindates: 0,
            acquiredTheoryLifekeys: 0,
            acquiredPracticeLifekeys: 0,

            numberOfScenarios: 0,
            scenariosDoneCount: 0,

            numberOfSteps: 0,
            numberOfDoneSteps: 0
        };

        courseIds
            .map(courseId => rootGetters['moduleCourse/getCourseStats'](courseId))
            .forEach(courseStats => {
                Object.keys(stats).forEach(key => {
                    stats[key] += courseStats[key];
                });
            });

        stats.acquiredVideoBraindates = (getters.getVideoBraindatesMap[lifeskillId] || []).length;

        stats.totalBraindates = stats.totalTheoryBraindates + stats.totalPracticeBraindates;
        stats.totalVideoLifekeys = 2; // NOTE: always 2, ignoring the possibility that the lifeskill does not have enough videos
        stats.acquiredVideoLifekeys = Math.min(
            stats.totalVideoLifekeys,
            Math.floor(stats.acquiredVideoBraindates / 25)
        );

        return stats;
    },

    getMarkedLifeskills: state => {
        return Object.entries(state.lifeskills)
            .filter(([key, value]) => value.follow)
            .map(([key, value]) => key)
            .sort();
    },

    follows: state => ls => {
        return state.lifeskills[ls].follow;
    },

    recommendedByMemolifeItems(state, getters, rootState, rootGetters) {
        const courseIds = ['top-10', 'ls-40-no', 'history-course', 'lang-group-a-no', 'ls-44-no', 'ls-02-no'];

        return courseIds
            .filter(courseId => {
                const courseStats = rootGetters['moduleCourse/getCourseStats'](courseId);
                return courseStats.theoryProgress < 1;
            })
            .map(courseId => {
                return {
                    courseId
                };
            });
    },

    recommendedItems: (state, getters, rootState, rootGetters) => {
        const courseIds = rootState.moduleCourse.courseIds.filter(courseId => {
            const course = rootState.moduleCourse.courses[courseId];
            const ls = course.lifeskillNumber;
            const courseStats = rootGetters['moduleCourse/getCourseStats'](courseId);
            return !courseStats.firstActiveJourneyId && getters.getMarkedLifeskills.indexOf(ls) > -1;
        });

        return getNextSuggestedSteps(courseIds, rootState, rootGetters);
    },

    recommendedVideos(state) {
        return state.recommendedVideos.map(v => {
            return {
                ...v,
                id: v.youtubeId,
                videoId: v.youtubeId,
                // videoId: v.id,
                lifeskillId: v.numbershape,
                duration: moment.duration(v.duration).asSeconds()
            };
        });
    },

    followedVideos(state, getters) {
        const videoBucket = [];
        const seen = {};
        getters.getMarkedLifeskills.forEach(ls => {
            const lsVideos = getters.getVideosForLifeskill(ls);
            const unwatched = [];
            const max = 3;
            for (let i = 0; lsVideos.length > i; i++) {
                const video = lsVideos[i];
                const vs = state.videos[video.id];
                if (vs && vs.progress) {
                    // pass
                } else if (!seen[video.id]) {
                    unwatched.push({ videoId: video.id, lifeskillId: ls });
                    seen[video.id] = true;
                }
                if (unwatched.length === max) {
                    break;
                }
            }
            videoBucket.push(...unwatched);
        });
        return videoBucket;
    },

    inProgressVideoItems(state, getters) {
        return state.videos_in_progress;
    },

    inProgressItems(state, getters, rootState, rootGetters) {
        const items = [
            ...rootGetters['moduleCourse/inProgressItems'],
            ...getters.inProgressVideoItems.map(v => ({ ...v, type: 'video' }))
        ];
        return items.sort((a, b) => moment(b.latestActivity).diff(moment(a.latestActivity)));
    },

    activeScenarios(state, getters, rootState) {
        let activeScenarios = [];
        rootState.moduleCourse.courseIds.forEach(courseId => {
            const course = rootState.moduleCourse.courses[courseId];
            if (course) {
                if (course.currentScenarioInfo && !course.currentScenarioInfo.pending) {
                    activeScenarios.push(course.currentScenarioInfo);
                }
            }
        });
        return activeScenarios;
    },

    notifications(state, getters, rootState) {
        let notifications = [];

        getters.availableLanguages.forEach(languageId => {
            const language = state.languages[languageId];
            const dueRepetitionCount = Object.values(language.repetitions).filter(repetition => repetition.due).length;
            if (dueRepetitionCount > 0) {
                notifications.push({
                    componentName: 'DashboardLanguageRepetitionsNotification',
                    props: { dueRepetitionCount, languageId },
                    text: `You have ${dueRepetitionCount} repetitions pending in ${languageId}`
                });
            }
        });

        getters.activeScenarios.forEach(activeScenario => {
            notifications.push({
                componentName: 'DashboardScenarioNotification',
                props: {
                    stepId: activeScenario.stepId,
                    scenarioIndex: activeScenario.scenarioIndex
                    // time: '12 min ago'
                }
            });
        });

        Object.values(rootState.moduleCourse.stepGroups).forEach(stepGroup => {
            if (stepGroup.repetition && stepGroup.repetition.due) {
                notifications.push({
                    componentName: 'DashboardStepGroupRepetitionNotification',
                    props: {
                        stepGroupId: stepGroup.id
                        // time: '12 min ago'
                    }
                });
            }
        });

        return notifications;
    },

    availableLanguagesFlytoget(state) {
        return state.memolanguage.availableLanguages.filter(languageId => {
            if (languageId === 'english') {
                return false;
            } else if (languageId === 'norwegian') {
                return false;
            }
            return true;
        });
    },
    availableLanguages(state) {
        return state.memolanguage.availableLanguages;
    },

    // local state stuff
    getLifeskillSelectedVideo: (state, getters) => lifeskillId => {
        return state.lifeskills[lifeskillId].selectedVideoId;
    },

    braindates(state) {
        return state.braindates;
    },

    streakLength(state) {
        return state.memostreak;
    },

    getBraindatesInTimeRange: (state, getters) => (start, end) => {
        return state.braindates.filter(({ createdAt }) => {
            return moment(createdAt).isBetween(start, end);
        });
    },
    braindatesForCurrentDay: (state, getters) => {
        return getters
            .getBraindatesInTimeRange(state.time.startOfCurrentDay, state.time.endOfCurrentDay)
            .filter(bd => !bd.isBonus);
    },
    braindatesForCurrentWeek: (state, getters) => {
        return getters
            .getBraindatesInTimeRange(state.time.startOfCurrentWeek, state.time.endOfCurrentWeek)
            .filter(bd => !bd.isBonus);
    },
    braindatesForCurrentMonth: (state, getters) => {
        return getters.getBraindatesInTimeRange(state.time.startOfCurrentMonth, state.time.endOfCurrentMonth);
    },
    locale: state => {
        return state.memolife.appLanguage || 'en';
    },
    gettingStartedTotalProgress: (state, getters, rootState) => {
        let items = getters['gettingStartedItems'];
        return items.filter(item => item.progress === 1).length / items.length;
        // return items.reduce((c, item) => c + item.progress, 0) / items.length
    },
    gettingStartedHasFocus(state, getters, rootState, rootGetters) {
        const dashboardHasVisibleSectionsAboveGettingStarted =
            rootGetters['moduleMemoransier/publishedGames'].length > 0;

        return (
            !state.memolife.didExitGettingStartedFocus &&
            !dashboardHasVisibleSectionsAboveGettingStarted &&
            getters.gettingStartedTotalProgress < 1
        );
    },

    gettingStartedItems(state, getters, rootState, rootGetters) {
        const mSettings = rootState.moduleMemogenius.settings;
        return [
            {
                id: 'setDailyGoal',
                progress: state.memolife.dailyRequiredBraindatesNotSetByUser ? 0 : 1,
                locked: 0
            },
            {
                id: 'memoryiqTheory',
                progress: mSettings.memoryiqTheory ? 1 : 0,
                locked: 0
            },
            {
                id: 'memoryiqPractice',
                progress: mSettings.memoryOnboardingTestB ? 1 : 0,
                locked: !mSettings.memoryiqTheory ? 1 : 0
            },
            {
                id: 'firstVideo',
                progress: getters['getOnboardingVideoBraindate'] === null ? 0 : 1,
                locked: 0
            },
            {
                id: 'selectFavorites',
                progress: state.memolife.didSelectFavoriteLifeskills ? 1 : 0,
                locked: 0
            }
        ];
    },

    gettingStartedItemsOLD(state, getters, rootState, rootGetters) {
        const step0 = rootState.moduleCourse.steps['ls-40-no:0'];
        const step1 = rootState.moduleCourse.steps['ls-40-no:1'];
        const stepGroup1 = rootState.moduleCourse.stepGroups['ls-40-no:group1'];
        const course = rootState.moduleCourse.courses['ls-40-no'];

        if (!(step1 && stepGroup1 && course)) {
            return [];
        }

        const mSettings = rootState.moduleMemogenius.settings;
        const braindates = state.braindates;

        return [
            {
                id: 'selectFavorites',
                progress: state.memolife.didSelectFavoriteLifeskills ? 1 : 0,
                locked: 0
            },
            {
                id: 'setDailyGoal',
                progress: state.memolife.dailyRequiredBraindatesNotSetByUser ? 0 : 1,
                locked: 0
            },
            {
                id: 'doubleYourMemory',
                progress: (mSettings.memoryOnboardingTestA ? 0.5 : 0) + (mSettings.memoryOnboardingTestB ? 0.5 : 0),
                locked: 0
            },
            {
                id: 'firstRead',
                progress: step1.read && step0.read.progress === 1 ? 1 : 0,
                locked: 0
            },
            {
                id: 'firstMemo',
                progress: step1.memorize && step1.memorize.progress === 1 ? 1 : 0,
                locked: 0
            },
            {
                id: 'firstQuiz',
                progress: step1.quiz && step1.quiz.progress === 1 ? 1 : 0,
                locked: 0
            },
            {
                id: 'firstVideo',
                progress: braindates.some(bd => bd.context === 'video') ? 1 : 0,
                locked: 0
            },
            {
                id: 'firstGame',
                progress: braindates.some(bd => bd.context === 'game') ? 1 : 0,
                locked: 0
            },
            {
                id: 'firstGroupQuiz',
                extraKey: 'after4Steps',
                progress: stepGroup1.quiz && stepGroup1.quiz.done ? 1 : 0,
                locked: 1 - rootGetters['moduleCourse/getStepGroupStats']('ls-00-no:group1').quizUnlockProgress
            },
            {
                id: 'firstScenario',
                extraKey: 'afterGroupQuiz',
                progress: braindates.some(bd => bd.context === 'course' && bd.activity === 'scenario') ? 1 : 0,
                locked: course.currentScenario && !course.currentScenario.pending ? 0 : 1
            }
        ];
    },
    autoplayVideoParts: state => {
        return state.memolife.autoplayVideoParts;
    },

    lifekeyCount(state, getters, rootState, rootGetters) {
        return state.lifekeys.length;
    },

    lifekeys: state => state.lifekeys,

    belt: (state, getters) => {
        return getBeltFromTotalPoints(getters.lifekeyCount);
    }
};
